import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

import { useTranslation } from 'react-i18next';

import { homeTarget, ShareHomeButton } from '@/components/ShareHomeButton';
export default function SharedReflectionPage() {
  const {
    t
  } = useTranslation();

  const { shareId } = useParams();
  const navigate = useNavigate();
  const [reflection, setReflection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const res = await axios.get(`${window.location.origin}/api/reflections/share/${shareId}`);
        setReflection(res.data);
      } catch (err) {
        setError('This reflection is no longer available.');
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: '#FFF8F0' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#C0724A' }} />
      </div>
    );
  }

  if (error || !reflection) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center gap-4 px-6" style={{ background: '#FFF8F0', color: '#3D2B1F' }}>
        <BookOpen className="h-12 w-12" style={{ color: '#C0724A', opacity: 0.5 }} />
        <p className="text-base">{error || 'Reflection not found.'}</p>
        <Button
          onClick={() => navigate(homeTarget(), { replace: true })}
          className="rounded-xl px-5 py-2.5 text-sm font-medium"
          style={{ background: '#C0724A', color: '#fff' }}
        >
          {t('generated.sharedreflectionpage_explore_eili_891e')}
        </Button>
      </div>
    );
  }

  const dateStr = new Date(reflection.date + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <div
      className="page-container"
      style={{ background: '#FFF8F0', color: '#3D2B1F' }}
      data-testid="shared-reflection-page"
    >
      <ShareHomeButton />
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-10 md:py-16 page-content w-full">
        {/* Header */}
        <div className="flex items-center gap-2 mb-8">
          <img src="/logo.png" alt={t('generated.sharedreflectionpage_eili_682d')} className="h-7 w-7 object-contain" />
          <span
            className="text-lg font-bold"
            style={{ fontFamily: "'Playwrite ES Deco', cursive", color: '#C0724A' }}
          >
            {t('generated.sharedreflectionpage_eili_682d')}
          </span>
          <span className="text-xs ml-1" style={{ color: '#8B6C5C' }}>
            {t('generated.sharedreflectionpage_daily_reflection_992e')}
          </span>
        </div>

        {/* Date */}
        <p
          className="text-xs uppercase tracking-widest mb-3 font-medium"
          style={{ color: '#C0724A' }}
        >
          {dateStr}
        </p>

        {/* Topic */}
        <h1
          className="text-2xl md:text-3xl font-semibold tracking-tight mb-4"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#3D2B1F' }}
        >
          {reflection.topic}
        </h1>

        {/* Introduction */}
        <p
          className="text-base leading-relaxed mb-6 italic"
          style={{ color: '#8B6C5C' }}
          dangerouslySetInnerHTML={{
            __html: reflection.introduction?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') || ''
          }}
        />

        <div className="border-t mb-6" style={{ borderColor: '#E8D5C4' }} />

        {/* Body */}
        <div
          className="text-base leading-relaxed whitespace-pre-line mb-8"
          style={{ color: '#3D2B1F' }}
          dangerouslySetInnerHTML={{
            __html: reflection.body
              ?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              .replace(/\n/g, '<br/>') || ''
          }}
        />

        {/* Thought to Consider */}
        <div
          className="rounded-xl p-5 mb-8"
          style={{ background: '#F5E6D8', border: '1px solid #E8D5C4' }}
        >
          <p className="text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: '#C0724A' }}>
            {t('generated.sharedreflectionpage_something_to_consider_e150')}
          </p>
          <p className="text-base leading-relaxed italic" style={{ color: '#3D2B1F' }}>
            {reflection.thought_to_consider}
          </p>
        </div>

        {/* Scripture References */}
        {reflection.scripture_references?.length > 0 && (
          <div className="mb-10">
            <p className="text-xs uppercase tracking-widest mb-3 font-medium" style={{ color: '#C0724A' }}>
              {t('generated.sharedreflectionpage_scripture_references_fed6')}
            </p>
            <div className="flex flex-wrap gap-2">
              {reflection.scripture_references.map((ref, i) => (
                <span
                  key={i}
                  className="text-sm px-3 py-1.5 rounded-lg"
                  style={{ background: '#F5E6D8', color: '#8B4513', border: '1px solid #E8D5C4' }}
                >
                  {ref}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div
          className="border-t pt-8 text-center"
          style={{ borderColor: '#E8D5C4' }}
        >
          <p className="text-sm mb-4" style={{ color: '#8B6C5C' }}>
            {t('generated.sharedreflectionpage_explore_deeper_biblical_f330')}
          </p>
          <Button
            onClick={() => navigate(homeTarget(), { replace: true })}
            className="rounded-xl px-6 py-3 text-sm font-medium gap-2"
            style={{ background: '#C0724A', color: '#fff' }}
            data-testid="shared-reflection-cta"
          >
            {t('generated.sharedreflectionpage_try_eili_c33b')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
